import 'dayjs/locale/ja';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HttpStatusCode } from 'axios';
import { Alert, Button, Modal, message, Row, Col, Select, Input, Tooltip } from 'antd';
import styles from './index.module.scss';
import { useAccountRegisterStore } from './account_register_store';
import { useIndicator } from '../../../hook/use_indicator';
import { ApiError } from '../../../api/api';
import { Messages } from '../../../constant/messages';
import { BusinessTypeKeys, BusinessTypes, RoleKeys, Roles_A, Roles_B, Roles_Z } from '../../../constant';
import { AccountRegisterRequest } from '../../../model/account_register_request';
import { useSessionStore } from '../../../context/session';
import { checkEmail, checkPassword } from '../../../utils/check_utils';
import { CompanyListResponse } from '../../../model/company_response';
/**
 * アカウント追加画面
 * @constructor
 */
export const AccountRegisterPage: React.FC = () => {
  const navigator = useNavigate();
  const { getCompanies, registerAccount } = useAccountRegisterStore((selector) => selector);
  const indicator = useIndicator();
  const [modal, showModal] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { loginInfo } = useSessionStore((selector) => selector);
  const [param, setParam] = useState<AccountRegisterRequest>();
  const [companies, setCompanies] = useState<CompanyListResponse>([]);
  // 会社プルダウン
  const [companyOptions, setCompanyOptions] = useState<{ value: number; label: string }[]>([]);
  // 権限プルダウン
  const [roleOptions, setRoleOptions] = useState<{ value: RoleKeys; label: string }[]>([]);
  // 会社プルダウン取得エラー
  const [isErrorCompanies, setIsErrorCompanies] = useState(false);
  // 会社プルダウン初期表示用
  const [selectedCompany, setSelectedCompany] = useState<number>();
  // 権限プルダウン初期表示用
  const [selectedRole, setSelectedRole] = useState<RoleKeys>(null);
  // 入力チェックエラー用
  const [passwordError, setPasswordError] = useState<boolean | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<boolean | null>(null);
  const [emailError, setEmailError] = useState<boolean | null>(null);
  const [accountError, setAccountError] = useState<boolean | null>(null);
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const emptyCompany: { value: number; label: string } = { value: 0, label: ' ' };
  const emptyRole: { value: RoleKeys, label: string } = { value: null, label: ' ' };

  useEffect(() => {
    // 会社一覧取得
    getCompanies().then((res) => {
      setCompanies(res);

      if (res.length === 0) {
        // 空の場合はエラー
        setIsErrorCompanies(true);
      }
      const options = res.map((company: { id: number; name: string, businessType: BusinessTypeKeys }) => {
        const businessName = BusinessTypes.find((f) => f.key === company.businessType)?.value ?? '不明';
        return {
          value: company.id,
          label: `${company.name} - ${businessName}`,
        };
      });
      setCompanyOptions([emptyCompany, ...options]);
      setSelectedCompany(0);
      setParam((param) => ({ ...param, companyId: 0 }));
    }).catch((e) => {
      if (e instanceof ApiError) {
        if (e.response.status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        }
        setIsErrorCompanies(true);
      }
    });
  }, []);

  useEffect(() => {
    // 権限一覧の設定
    const roleId = loginInfo?.user?.role.roleId ?? RoleKeys.none;
    const roles: { value: RoleKeys; label: string }[] = ((roleId: string) => {
      switch (roleId) {
        case RoleKeys.administrator:
          return Roles_Z;
        case RoleKeys.generalmanager:
          return Roles_A;
        default:
          return Roles_B;
      }
    })(roleId).map((role) => {
      return {
        value: role.key,
        label: role.value,
      };
    }).filter((role) => {
      const company = companies.find((c) => c.id === selectedCompany) ?? { businessType: BusinessTypeKeys.none };
      if (company.businessType === BusinessTypeKeys.none) {
        return false;
      }
      if (company.businessType === BusinessTypeKeys.all) {
        return [RoleKeys.administrator, RoleKeys.generalmanager].includes(role.value);
      }

      return [RoleKeys.manager, RoleKeys.worker_read, RoleKeys.worker_write].includes(role.value);
    });

    setRoleOptions([emptyRole, ...roles]);
    setSelectedRole(null);
    setParam((param) => ({ ...param, role: null }));
  }, [companies, selectedCompany]);

  // パスワード入力規則チェック
  const validatePassword = (value: string) => {
    if (value.length === 0) {
      setPasswordError(null);
      return;
    }
    const isValid = checkPassword(value);
    setPasswordError(!isValid);
  };

  const validatePasswordConfirm = useCallback(() => {
    if (!param?.password || !passwordConfirm) {
      setConfirmPasswordError(null);
      return;
    }
    setConfirmPasswordError(param.password !== passwordConfirm);
  }, [param, passwordConfirm]);

  // メールフォーマットチェック
  const validateEmail = (value: string) => {
    if (value.length === 0) {
      setEmailError(null);
      return;
    }
    setEmailError(!checkEmail(value));
  };

  const validateAccountId = (value: string) => {
    if (value.length === 0) {
      setAccountError(null);
      return;
    }
    const regex = /^[a-zA-Z\d!@#$%^&*()_+\-=[\]{}|]+$/;
    setAccountError(!regex.test(value));
  };

  const isFormValid = useMemo(() => {
    return (
      param?.accountId
      && param?.password
      && passwordConfirm
      && param?.userName
      && param?.companyId
      && param?.role
      && param?.email
      && passwordError === false
      && confirmPasswordError === false
      && emailError === false
      && accountError === false
    );
  }, [param, passwordError, confirmPasswordError, emailError, accountError]);

  /** 登録処理 */
  const handleRegister = () => {
    indicator.show();
    registerAccount(param).then(() => {
      // 完了メッセージ
      messageApi.success(Messages.EXECUTE_ISSUE_REGISTER);
      // 画面リロード
      navigator('/account_list', { state: { refresh: true } });

    }).catch((e) => {
      if (e instanceof ApiError) {
        const { status } = e.response;
        if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
        } else if (status === HttpStatusCode.Conflict) {
          window.alert(Messages.ERR_DIALOG_ALREADY_REGISTER);
        } else if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        } else {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
        }
      }
    }).finally(() => {
      indicator.hide();
    });
  };

  const onChangeCompany = useCallback((value: number) => {
    setParam((param) => ({ ...param, companyId: value }));
    setSelectedCompany(value);
  }, []);

  const showDescription = useCallback((targetRole: RoleKeys, elem: React.ReactNode) => {
    if (roleOptions.find((r) => r.value === targetRole) !== undefined) {
      return elem;
    }
    return null;
  }, [roleOptions]);

  return (
    <div>
      {contextHolder}
      <h1>アカウント追加画面</h1>
      <Modal
        open={modal}
        title="権限について"
        width={800}
        onCancel={() => showModal(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => showModal(false)}>
            OK
          </Button>,
        ]}
      >
        <div>
          <ul className={styles.role_explan_list}>
            {showDescription(
              RoleKeys.worker_read, (
                <>
                  <li>業務実施会社(閲覧のみ)</li>
                  <ul>ユーザが作業を実施する会社であり、実作業を実施する場合に指定します。<br/>自社作業に関する情報閲覧が可能です。</ul>
                </>
              ),
            )}
            {showDescription(
              RoleKeys.worker_write, (
                    <>
                      <li>業務実施会社(更新可)</li>
                      <ul>ユーザが作業を実施する会社であり、実作業・報告業務を行う場合に指定します。<br/>自社作業に関する情報の投入・更新が可能です。</ul>
                    </>
              ),
            )}
            {showDescription(
              RoleKeys.manager, (
                    <>
                      <li>業務実施責任会社</li>
                      <ul>ユーザが特定の業務を取りまとめる会社である場合に指定します。<br/>担当業務の全ての情報の閲覧・更新、担当業務に所属するすべてのアカウント管理が可能です。</ul>
                    </>
              ),
            )}
            {showDescription(
              RoleKeys.generalmanager, (
                    <>
                      <li>統括管理者</li>
                      <ul>ユーザがすべての業務を取りまとめる会社である場合に指定します。<br/>各業務情報の閲覧と、統括管理者に所属するすべてのアカウント管理が可能です。</ul>
                    </>
              ),
            )}
            {showDescription(
              RoleKeys.administrator, (
                    <>
                      <li>システム管理者</li>
                      <ul>ユーザがシステム管理者である場合に指定します。<br/>他権限が更新可能なすべての情報の更新、すべてのアカウント管理が可能です。</ul>
                    </>
              ),
            )}
          </ul>
        </div>
      </Modal>

      <div className={styles.section}>
        <form>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              アカウント名<label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Tooltip title="ログイン時に使用するアカウント名を入力してください。">
              <Input
                className={styles.inputWidth}
                onChange={(e) => setParam((param) => {
                  return { ...param, accountId: e.target.value };
                })}
                onBlur={() => {
                  validateAccountId(param?.accountId ?? '');
                }}
              />
              </Tooltip>
            </Col>
          </Row>
          {accountError && (
              <Row className={styles.row}>
                <div className={`${styles.formLabel} ${styles.item}`}></div>
                <Col className={`${styles.item} ${styles.form_custom}`}>
                  <div style={{ textAlign: 'left', fontSize: '12px', color: 'red' }}>
                    アカウント名は入力規則に準拠してください。
                  </div>
                </Col>
              </Row>
          )}
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}></div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <div style={{ textAlign: 'left', fontSize: '12px', color: '#888' }}>
                アカウント名には、英字（A〜Z、a〜z）、数字（0〜9）、記号は !@#$%^&*()_+-=[]{'{}'}| が利用可能です。
              </div>
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              パスワード<label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Tooltip title="ログイン時に使用するパスワードを入力してください。">
              <Input.Password
                className={styles.inputWidth}
                onChange={(e) => {
                  setParam((param) => {
                    return { ...param, password: e.target.value };
                  });
                }}
                onBlur={() => {
                  validatePassword(param?.password ?? '');
                  validatePasswordConfirm();
                }}
              />
              </Tooltip>
            </Col>
          </Row>
          {passwordError && (
            <Row className={styles.row}>
              <div className={`${styles.formLabel} ${styles.item}`}></div>
              <Col className={`${styles.item} ${styles.form_custom}`}>
                <div style={{ textAlign: 'left', fontSize: '12px', color: 'red' }}>
                  パスワードは入力規則に準拠してください。
                </div>
              </Col>
            </Row>
          )}
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              パスワード（確認用）<label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Tooltip title="パスワードをもう一度入力してください。">
              <Input.Password
                className={styles.inputWidth}
                onChange={(e) => {
                  setPasswordConfirm(e.target.value);
                }}
                onBlur={() => {
                  validatePasswordConfirm();
                }}
              />
              </Tooltip>
              <br/>
            </Col>
          </Row>
          {confirmPasswordError && (
            <Row className={styles.row}>
              <div className={`${styles.formLabel} ${styles.item}`}></div>
              <Col className={`${styles.item} ${styles.form_custom}`}>
                <div style={{ textAlign: 'left', fontSize: '12px', color: 'red' }}>
                  パスワードとパスワード（確認用）が一致しません。
                </div>
              </Col>
            </Row>
          )}
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}></div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <div style={{ textAlign: 'left', fontSize: '12px', color: '#888' }}>
                パスワードは、7文字以上で英字（A〜Z、a〜z）および数字（0〜9）の両方を含めて設定してください。<br/>
                記号は !@#$%^&*()_+-=[]{'{}'}| が利用可能です。
              </div>
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              ユーザ名<label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Tooltip title="システム内で表示されるユーザ名を入力してください。">
              <Input
                className={styles.inputWidth}
                onChange={(e) => setParam((param) => {
                  return { ...param, userName: e.target.value };
                })}
              />
              </Tooltip>
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              会社名<label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              {
                (isErrorCompanies)
                  ? <Alert message={Messages.ERR_OTHER} type="error" showIcon/>
                  : <Tooltip title="ユーザの所属する会社を選択してください。">
                  <Select
                    style={{ width: 300 }}
                    value={selectedCompany}
                    options={companyOptions}
                    onChange={onChangeCompany}
                  />
                  </Tooltip>
              }
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              権限<label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Tooltip title={'会社を選択後、ユーザに付与する権限を選択してください。'}>
                <Select<RoleKeys>
                  style={{ width: 250 }}
                  value={selectedRole}
                  disabled={selectedCompany === 0}
                  options={roleOptions}
                  onChange={(value) => {
                    setParam((param) => ({ ...param, role: value }));
                    setSelectedRole(value);
                  }}
                />
              </Tooltip>
              <Button style={{ marginLeft: 10 }} disabled={selectedCompany === 0} onClick={() => {
                showModal(true);
              }}>権限について</Button>
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={`${styles.formLabel} ${styles.item}`}>
              メールアドレス<label className={styles.required}/>
            </div>
            <Col className={`${styles.item} ${styles.form_custom}`}>
              <Tooltip title="ユーザのメールアドレスを入力してください。">
              <Input
                className={styles.inputWidth}
                onChange={(e) => {
                  setParam((param) => {
                    return { ...param, email: e.target.value };
                  });
                }}
                onBlur={() => {
                  validateEmail(param?.email ?? '');
                }}
              />
              </Tooltip>
            </Col>
          </Row>
          {emailError && (
            <Row className={styles.row}>
              <div className={`${styles.formLabel} ${styles.item}`}></div>
              <Col className={`${styles.item} ${styles.form_custom}`}>
                <div style={{ textAlign: 'left', fontSize: '12px', color: 'red' }}>
                  正しいメールアドレスを入力してください。
                </div>
              </Col>
            </Row>
          )}
          <br/>
          <br/>
          <div className={styles.register_button}>
            <Button onClick={() => navigator('/account_list')}>戻る</Button>
            <Button
              loading={indicator.showing}
              type={'primary'}
              disabled={!isFormValid}
              onClick={handleRegister}
            >
              アカウント追加
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
